import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Flame, Mail, MessageSquareHeart, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const Icon = ({ t, className }) => (t === "positive_reply" ? <MessageSquareHeart className={className} /> : <Flame className={className} />);

const DigestToggle = () => {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/rcc/alerts/settings").then((r) => setS(r.data || {})).catch(() => setS({})); }, []);
  if (!s) return null;
  const set = async (on) => {
    const body = { daily_digest: on, email: s.email || user?.email, hour: 8, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
    setS({ ...s, ...body });
    try { await api.put("/admin/rcc/alerts/settings", body); toast.success(on ? `Daily digest at 8am to ${body.email}` : "Daily digest off"); }
    catch (e) { setS(s); toast.error(e.response?.status === 404 ? "Email digest isn't live on the backend yet (spec §33)." : errMsg(e)); }
  };
  return (
    <label className="flex items-center justify-between gap-2 border-t border-[#27272A] px-3 py-2.5 text-xs text-zinc-300" data-testid="rcc-digest">
      <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[#c79bff]" />Email me a daily digest at 8am</span>
      <Switch checked={!!s.daily_digest} onCheckedChange={set} data-testid="rcc-digest-toggle" />
    </label>
  );
};

export const AlertBanner = ({ unread, markRead }) => {
  const a = unread[0];
  if (!a) return null;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#8224e3]/60 bg-[#8224e3]/15 p-3 shadow-[0_0_24px_rgba(130,36,227,0.35)] animate-in fade-in slide-in-from-top-2" data-testid="rcc-alert-banner">
      <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#8224e3] text-white"><span className="absolute inset-0 animate-ping rounded-full bg-[#8224e3]/50" /><Icon t={a.type} className="relative h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-zinc-50" data-testid="rcc-alert-banner-title">{a.title}</p>
        <p className="truncate text-xs text-[#d7b6ff]">{a.detail}{unread.length > 1 ? ` · +${unread.length - 1} more` : ""}</p>
      </div>
      <Link to={a.link || "/admin/rcc"} onClick={() => markRead([a.id])} className="shrink-0 rounded-full bg-[#8224e3] px-3 py-1 text-xs font-semibold text-white hover:bg-[#9440f0]" data-testid="rcc-alert-banner-view">View</Link>
      <button onClick={() => markRead([a.id])} className="shrink-0 text-zinc-400 hover:text-zinc-100" data-testid="rcc-alert-banner-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
};

export const AlertBell = ({ items, unread, markRead }) => {
  const isNew = new Set(unread.map((a) => a.id));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative rounded-full border border-[#27272A] p-2 text-zinc-300 hover:border-[#8224e3]/60 hover:text-[#d7b6ff]" data-testid="rcc-alert-bell">
          <Bell className="h-4 w-4" />
          {unread.length > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#8224e3] px-1 text-[10px] font-bold text-white" data-testid="rcc-alert-count">{unread.length > 99 ? "99+" : unread.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-[#8224e3]/40 bg-[#0d0a12] p-0" data-testid="rcc-alert-list">
        <div className="flex items-center justify-between border-b border-[#27272A] px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#d7b6ff]">Signal alerts</p>
          {unread.length > 0 && <button onClick={() => markRead(unread.map((a) => a.id))} className="text-[11px] text-zinc-400 hover:text-zinc-100" data-testid="rcc-alert-mark-all">Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {!items.length ? <p className="p-4 text-xs text-zinc-500" data-testid="rcc-alert-empty">No alerts yet. Hot companies and positive replies show up here.</p>
            : items.map((a, i) => (
              <Link key={a.id} to={a.link || "/admin/rcc"} onClick={() => markRead([a.id])} className={cn("flex gap-2 border-b border-[#1f1f23] px-3 py-2.5 hover:bg-[#8224e3]/10", isNew.has(a.id) && "bg-[#8224e3]/10")} data-testid={`rcc-alert-item-${i}`}>
                <Icon t={a.type} className={cn("mt-0.5 h-4 w-4 shrink-0", a.type === "positive_reply" ? "text-emerald-400" : "text-red-400")} />
                <span className="min-w-0"><span className="block truncate text-sm text-zinc-100">{a.title}</span>{a.detail && <span className="block truncate text-[11px] text-zinc-500">{a.detail}</span>}</span>
                {isNew.has(a.id) && <span className="ml-auto mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#8224e3]" />}
              </Link>
            ))}
        </div>
        <DigestToggle />
      </PopoverContent>
    </Popover>
  );
};
